import { toStringWithCommas } from "@/utils/strings/number"

// キャンセル料お支払い 10 万円ルーレット (請求側) の表示に使う値.
// 請求画面 (GET /public/payment/{id}) と支払い完了画面 (POST /public/payment/confirm) の
// lottery を同じ形で受け取る. backend: service/PaymentService.py _cancel_fee_lottery_res.
export type CancelFeeLottery = {
	// 請求時にサーバが抽選して DB に保存した当選確率の倍率. 何度開いても同じ値.
	multiplier: number
	// 当選者 1 名が受け取る賞品の金額 (円)
	prizePrice: number
	// ルーレットの盤面に並べる倍率 (12 時方向から時計回り)
	wheelMultipliers: number[]
	// お支払い日 (cancel.paid_time, JST, "YYYY/MM/DD"). 完了画面で
	// 「7日以内に再予約・30日以内にご来店」の起算日として見せる. 支払い前 (請求画面) は null.
	paidDate: string | null
	// 請求画面を開いた直後にルーレットを自動で開いて回し始めるか (見せ方の A/B テストの群).
	// 請求時にサーバが振り分けて lottery_tickets.roulette_display に保存した値で、再訪・別端末でも同じ.
	// false = バナーの「ルーレットを回す」ボタンを押した時だけ開く (現行の見せ方 = 対照群).
	autoOpenRoulette: boolean
}

type CancelFeeLotteryResponse = {
	multiplier: number
	prize_price: number
	wheel_multipliers: number[]
	paid_date?: string | null
	auto_open_roulette?: boolean
}

// lottery の請求でない / 支払い期限を過ぎた / 応募が成立していない時は null が返る.
export const toCancelFeeLottery = (
	res: CancelFeeLotteryResponse | null | undefined
): CancelFeeLottery | null => {
	if (res === null || res === undefined) {
		return null
	}
	return {
		multiplier: res.multiplier,
		prizePrice: res.prize_price,
		wheelMultipliers: res.wheel_multipliers,
		paidDate: res.paid_date ?? null,
		// A/B 開始前に発行されたくじ券 (振り分けなし) は自動で開かない (現行の見せ方のまま) ので、無ければ false.
		autoOpenRoulette: res.auto_open_roulette ?? false,
	}
}

// キャンペーンの名称・当選人数は、店舗向け LP
// (infra/pages/cancel_fee_lottery_campaign_page.py) の記載に合わせている.
// 再予約側 (rereservation/RereservationIncentiveBanner/lotteryPrize.ts) とは当選人数が違うので共有しない.
export const CANCEL_FEE_LOTTERY_CAMPAIGN_NAME =
	"キャンセル料お支払いキャンペーン"

export const CANCEL_FEE_LOTTERY_WINNER_TEXT = "毎月 1 名様"

// 賞品のギフト. 規約 (CancelFeeLotteryTermsSection) の提供主体・注意事項は再予約側と同じ
// 「えらべるマネー」(AnyGift Business) 前提なので、再予約側と揃える.
// (店舗向け LP は「えらべるPay」と書いているので、LP 側も揃える必要がある)
export const CANCEL_FEE_LOTTERY_PRIZE_GIFT_NAME = "えらべるマネー"

// 賞品は現金ではなくポイントなので、「10 万円が当たる」とは書かない
// (再予約側 lotteryPrize.ts と同じ 2026-09-29 定例の決定).
// 例: 100000 → "100,000円分のポイント"
export const cancelFeeLotteryPrizeText = (price: number): string =>
	`${toStringWithCommas(price)}円分のポイント`

// お支払い日から、この日数以内に「再予約」すると当選確率がさらに上がる (店舗向け LP の記載).
// ご来店自体の期限は CANCEL_FEE_LOTTERY_REVISIT_VISIT_DAYS (30日) で別に持つ
// (「来店も7日以内なのか」が曖昧で問い合わせに繋がるため、2026-10-15 に起点と期限を明文化した).
export const CANCEL_FEE_LOTTERY_REVISIT_DAYS = 7

// お支払い日から、この日数以内に「ご来店」すると当選確率アップの対象になる.
export const CANCEL_FEE_LOTTERY_REVISIT_VISIT_DAYS = 30

// 当選者への SMS 連絡の期限として画面に出す日数 (お支払い後◯日以内).
// 規約では「各月の対象お支払い分を翌月5日までに抽選・連絡」なので、
// 月初のお支払いでも最大 36 日程度 → 40 日以内で担保できる.
export const CANCEL_FEE_LOTTERY_WINNER_SMS_DAYS = 40

// 再予約してご来店いただいた時に、当選確率に掛かる倍率 (LP: 「さらに 2 倍 (最大 10 倍)」).
// NOTE: 2026-10-12 時点では backend で倍率を上乗せする処理は未実装
// (domain/model/lottery.py の TODO)。画面で案内するだけなので、backend の実装と揃えること.
export const CANCEL_FEE_LOTTERY_REVISIT_BOOST = 2
